import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { opApi } from "@/lib/operator";
import OperatorHeader from "@/components/OperatorHeader";
import { Search, Check, Loader2, AlertCircle, Tractor, MapPin, X, ArrowLeft } from "lucide-react";

const FIELDS = [
  { k: "field_name",  label: "Field / location", type: "text",   placeholder: "e.g. Lower 10" },
  { k: "acres",       label: "Acres",            type: "number", step: "0.1" },
  { k: "hours",       label: "Engine hours",     type: "number", step: "0.1" },
  { k: "fuel_litres", label: "Fuel (L)",         type: "number", step: "1" },
];

export default function OperatorNewEntry() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();
  const presetDate    = search.get("date");
  const presetMachine = { vin: search.get("machine_vin"), label: search.get("machine_label") };
  const presetClient  = { id: search.get("client_id"), name: search.get("client_name") };
  const presetTask    = search.get("task");
  const presetField   = search.get("field_name");
  const fromJob       = search.get("from_job");
  const editing = Boolean(id);

  const [step, setStep] = useState(1);
  const [machines, setMachines] = useState([]);
  const [clients, setClients]   = useState([]);
  const [tasks, setTasks]       = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [machineSearch, setMachineSearch] = useState("");
  const [taskSearch, setTaskSearch] = useState("");
  const [form, setForm] = useState({
    work_date: presetDate || new Date().toISOString().slice(0, 10),
    machine_vin: presetMachine.vin || null,
    machine_label: presetMachine.label || "",
    client_id: presetClient.id || null,
    client_name: presetClient.name || "",
    field_name: presetField || "", task: presetTask || "",
    acres: "", hours: "", fuel_litres: "", notes: "",
    gps_lat: null, gps_lon: null,
    from_job_id: fromJob || null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState(null);
  const [clientsMeta, setClientsMeta] = useState({ source: null, reason: null });

  useEffect(() => {
    Promise.all([
      opApi.get("/app/machines"),
      opApi.get("/app/clients"),
      opApi.get("/app/diary/tasks"),
      editing ? opApi.get("/app/diary").then(r => r.data.find(e => e.id === id)) : Promise.resolve(null),
    ])
      .then(([m, c, t, entry]) => {
        setMachines(m.data?.data || []);
        setClients(c.data?.data || []);
        setClientsMeta({ source: c.data?.source, reason: c.data?.reason });
        setTasks(t.data || []);
        if (entry) {
          setForm(f => ({ ...f, ...entry, acres: entry.acres ?? "", hours: entry.hours ?? "", fuel_litres: entry.fuel_litres ?? "" }));
          setStep(4);
        }
        // If pre-filled from a planned job (has machine + task + client) jump straight to details
        if (!entry && fromJob && presetMachine.vin && presetTask) {
          setStep(4);
        }
      })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [id, editing]);

  const filteredClients = clients.filter(c =>
    !clientSearch || (c.name || "").toLowerCase().includes(clientSearch.toLowerCase())
  );
  const filteredMachines = machines.filter(m =>
    !machineSearch ||
    (m.label || "").toLowerCase().includes(machineSearch.toLowerCase()) ||
    (m.model || "").toLowerCase().includes(machineSearch.toLowerCase())
  );
  const filteredTasks = tasks.filter(t =>
    !taskSearch || t.toLowerCase().includes(taskSearch.toLowerCase())
  );

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const captureGPS = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => setForm(f => ({ ...f, gps_lat: pos.coords.latitude, gps_lon: pos.coords.longitude })),
      () => {},
      { timeout: 8000 }
    );
  };

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const payload = {
        ...form,
        acres:       form.acres === "" ? null : Number(form.acres),
        hours:       form.hours === "" ? null : Number(form.hours),
        fuel_litres: form.fuel_litres === "" ? null : Number(form.fuel_litres),
      };
      if (editing) {
        await opApi.patch(`/app/diary/${id}`, payload);
      } else {
        await opApi.post("/app/diary", payload);
      }
      // If created from a planned job, mark that job complete
      if (!editing && form.from_job_id) {
        try { await opApi.post(`/app/jobs/${form.from_job_id}/complete`); } catch { /* non-fatal */ }
      }
      navigate(presetDate ? "/app/calendar" : "/app/diary");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <OperatorHeader back="/app" />
        <div className="grid place-items-center py-20 text-slate-500"><Loader2 className="animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <OperatorHeader back={presetDate ? "/app/calendar" : "/app"} />

      <div className="px-4 sm:px-6 py-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => step === 1 ? navigate(presetDate ? "/app/calendar" : "/app") : setStep(step - 1)}
                  data-testid="entry-back"
                  className="text-slate-600 hover:text-[#c41e3a] flex items-center gap-1 text-sm font-bold">
            <ArrowLeft size={16} /> {step === 1 ? "Cancel" : "Back"}
          </button>
          <div className="text-xs text-slate-500">Step {step} of 4</div>
        </div>

        {/* Step 1: Machine */}
        {step === 1 && (
          <>
            <h2 className="font-display font-black text-2xl text-[#1a1a5c] mb-1">Which machine?</h2>
            <p className="text-slate-500 text-sm mb-4">Working <strong className="text-[#c41e3a]">{new Date(form.work_date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })}</strong></p>
            <SearchBox value={machineSearch} onChange={setMachineSearch} placeholder="Search machines…" />
            <div className="space-y-2 mt-4">
              {filteredMachines.map(m => (
                <button key={m.vin} data-testid={`machine-${m.vin}`}
                        onClick={() => { setField("machine_vin", m.vin); setField("machine_label", m.label); setStep(2); }}
                        className="w-full text-left bg-white hover:bg-[#1a1a5c] hover:text-white border border-slate-200 hover:border-[#1a1a5c] rounded-xl px-5 py-4 active:scale-[0.99] transition-all flex items-center gap-3 group shadow-sm">
                  <Tractor size={20} className="text-[#c41e3a] group-hover:text-white shrink-0" />
                  <div className="flex-1">
                    <div className="font-bold">{m.label || m.vin}</div>
                    <div className="text-xs text-slate-500 group-hover:text-white/70">{m.model}{m.source === "manual" ? " · Manual" : ""}</div>
                  </div>
                </button>
              ))}
              <button onClick={() => { setField("machine_vin", null); setField("machine_label", "Other / Implement"); setStep(2); }}
                      className="w-full text-left bg-white hover:bg-slate-100 border-2 border-dashed border-slate-200 rounded-xl px-5 py-3 text-slate-500 text-sm">
                Other / no machine
              </button>
            </div>
          </>
        )}

        {/* Step 2: Client */}
        {step === 2 && (
          <>
            <h2 className="font-display font-black text-2xl text-[#1a1a5c] mb-1">Which customer?</h2>
            <p className="text-slate-500 text-sm mb-4">Pulled live from Xero.</p>
            <SearchBox value={clientSearch} onChange={setClientSearch} placeholder="Search customers…" />
            {clientsMeta.source && clientsMeta.source !== "live" && clientsMeta.source !== "cache" && (
              <div className="mt-3 bg-amber-50 border border-amber-300 text-amber-800 text-xs px-3 py-2 rounded">
                <AlertCircle size={12} className="inline mr-1" />
                Xero not connected yet. {clientsMeta.reason || "Type the customer name manually below."}
              </div>
            )}
            <div className="space-y-1.5 mt-4 max-h-96 overflow-y-auto">
              {filteredClients.map(c => (
                <button key={c.id} data-testid={`client-${c.id}`}
                        onClick={() => { setField("client_id", c.id); setField("client_name", c.name); setStep(3); }}
                        className="w-full text-left bg-white hover:bg-amber-50 border border-slate-200 hover:border-[#c41e3a] rounded-lg px-4 py-3 transition-all">
                  <div className="font-medium text-[#1a1a5c]">{c.name}</div>
                  {c.email && <div className="text-[11px] text-slate-500">{c.email}</div>}
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-bold">…or type manually</div>
              <div className="flex gap-2">
                <input value={form.client_name} onChange={e => setField("client_name", e.target.value)}
                       placeholder="Customer name"
                       className="flex-1 bg-white border border-slate-300 rounded-lg px-4 py-3 text-[#1a1a5c] placeholder-slate-400 focus:border-[#1a1a5c] focus:outline-none" />
                <button onClick={() => { setField("client_id", null); setStep(3); }}
                        disabled={!form.client_name}
                        className="bg-[#c41e3a] hover:bg-[#d92a47] disabled:opacity-30 text-white font-bold px-5 rounded-lg">
                  Next
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Task */}
        {step === 3 && (
          <>
            <h2 className="font-display font-black text-2xl text-[#1a1a5c] mb-1">What task?</h2>
            <p className="text-slate-500 text-sm mb-4">Pick the operation.</p>
            <SearchBox value={taskSearch} onChange={setTaskSearch} placeholder="Search tasks…" />
            <div className="grid grid-cols-2 gap-2 mt-4">
              {filteredTasks.map(t => (
                <button key={t} data-testid={`task-${t}`}
                        onClick={() => { setField("task", t); setStep(4); }}
                        className="bg-white hover:bg-[#1a1a5c] hover:text-white border border-slate-200 hover:border-[#1a1a5c] rounded-xl px-4 py-4 font-bold text-[#1a1a5c] active:scale-95 transition-all shadow-sm">
                  {t}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Step 4: Details */}
        {step === 4 && (
          <>
            <h2 className="font-display font-black text-2xl text-[#1a1a5c] mb-1">{editing ? "Edit entry" : "Details"}</h2>
            <p className="text-slate-500 text-sm mb-4">Fill in what you can — leave blank what you don't have.</p>

            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 text-xs space-y-1 shadow-sm">
              <div><span className="text-slate-500">Date:</span> <span className="text-[#1a1a5c] font-bold">{form.work_date}</span></div>
              <div><span className="text-slate-500">Machine:</span> <span className="text-[#1a1a5c] font-bold">{form.machine_label || "—"}</span></div>
              <div><span className="text-slate-500">Customer:</span> <span className="text-[#1a1a5c] font-bold">{form.client_name || "—"}</span></div>
              <div><span className="text-slate-500">Task:</span> <span className="text-[#1a1a5c] font-bold">{form.task || "—"}</span></div>
            </div>

            {/* Date field (editable) */}
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-1.5">Work date</label>
              <input type="date" value={form.work_date}
                     onChange={e => setField("work_date", e.target.value)} data-testid="field-work_date"
                     className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-[#1a1a5c] text-lg focus:border-[#1a1a5c] focus:outline-none" />
            </div>

            {FIELDS.map(f => (
              <div key={f.k} className="mb-4">
                <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-1.5">{f.label}</label>
                <input type={f.type} step={f.step} placeholder={f.placeholder}
                       inputMode={f.type === "number" ? "decimal" : "text"}
                       value={form[f.k]} onChange={e => setField(f.k, e.target.value)}
                       data-testid={`field-${f.k}`}
                       className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-[#1a1a5c] text-lg placeholder-slate-400 focus:border-[#1a1a5c] focus:outline-none" />
              </div>
            ))}

            <div className="mb-4">
              <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-1.5">Notes</label>
              <textarea rows={3} placeholder="Anything to flag for accounts…"
                        value={form.notes} onChange={e => setField("notes", e.target.value)}
                        data-testid="field-notes"
                        className="w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-[#1a1a5c] placeholder-slate-400 focus:border-[#1a1a5c] focus:outline-none" />
            </div>

            <button onClick={captureGPS} data-testid="capture-gps"
                    className="w-full bg-white hover:bg-slate-50 border border-slate-300 rounded-lg px-4 py-3 text-sm text-slate-600 flex items-center justify-center gap-2 mb-4">
              <MapPin size={14} /> {form.gps_lat ? `Pinned ${form.gps_lat.toFixed(4)}, ${form.gps_lon.toFixed(4)}` : "Pin my GPS location (optional)"}
            </button>

            {err && (
              <div className="bg-red-50 border border-[#c41e3a]/40 text-[#c41e3a] text-sm px-4 py-3 rounded-md mb-4 flex items-center gap-2">
                <X size={14} /> {err}
              </div>
            )}
          </>
        )}
      </div>

      {/* Sticky save bar */}
      {step === 4 && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t-2 border-slate-200 px-4 py-4 shadow-2xl">
          <div className="max-w-2xl mx-auto">
            <button onClick={save} disabled={saving} data-testid="save-entry"
                    className="w-full bg-[#c41e3a] hover:bg-[#d92a47] disabled:opacity-50 text-white font-display font-black text-lg py-4 rounded-xl flex items-center justify-center gap-2 active:scale-[0.99] transition-all shadow-lg">
              {saving ? <><Loader2 className="animate-spin" size={18} /> Saving…</> : <><Check size={20} /> {editing ? "Update entry" : "Save entry"}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
             className="w-full bg-white border border-slate-300 rounded-lg pl-11 pr-4 py-3 text-[#1a1a5c] placeholder-slate-400 focus:border-[#1a1a5c] focus:outline-none" />
    </div>
  );
}
